import { useUserProfileStore } from "@/stores/user-profile-store";
import { PLANNER_PERMISSIONS } from "@/constants/permission";

/**
 * Hook to check user permissions based on rolePermissions array
 * Similar to WOR-1792 implementation in Angular
 */
export const usePermissions = () => {
  const { userProfile } = useUserProfileStore();

  const hasRolePermission = (permission: string): boolean => {
    if (!userProfile?.rolePermissions) {
      return false;
    }
    return userProfile.rolePermissions.includes(permission);
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!userProfile?.rolePermissions) {
      return false;
    }
    return permissions.some((permission) =>
      userProfile.rolePermissions.includes(permission)
    );
  };

  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!userProfile?.rolePermissions) {
      return false;
    }
    return permissions.every((permission) =>
      userProfile.rolePermissions.includes(permission)
    );
  };

  // Specific permission checkers for WOR-1793
  const canViewPlant1 = (): boolean => {
    return hasRolePermission(PLANNER_PERMISSIONS.PLANT1_VIEW_JOB);
  };

  const canManagePlant1 = (): boolean => {
    return hasRolePermission(PLANNER_PERMISSIONS.PLANT1_MANAGE_JOB);
  };

  const canViewPlant3 = (): boolean => {
    return hasRolePermission(PLANNER_PERMISSIONS.PLANT3_VIEW_JOB);
  };

  const canManagePlant3 = (): boolean => {
    return hasRolePermission(PLANNER_PERMISSIONS.PLANT3_MANAGE_JOB);
  };

  const canManageEditableColumns = (): boolean => {
    return hasRolePermission(PLANNER_PERMISSIONS.PLANT3_EDITABLE_COLUMN);
  };

  const canManageProcesses = (): boolean => {
    return hasRolePermission(PLANNER_PERMISSIONS.PROCESS_MANAGEMENT);
  };

  const canManageTechnicians = (): boolean => {
    return hasRolePermission(PLANNER_PERMISSIONS.TECHNICIAN_MANAGE);
  };

  const canManageMachines = (): boolean => {
    return hasRolePermission(PLANNER_PERMISSIONS.MACHINE_MANAGE);
  };

  return {
    hasRolePermission,
    hasAnyPermission,
    hasAllPermissions,
    canViewPlant1,
    canManagePlant1,
    canViewPlant3,
    canManagePlant3,
    canManageEditableColumns,
    canManageProcesses,
    canManageTechnicians,
    canManageMachines,
  };
};
